import React, {useMemo} from 'react';
import {useSelector} from 'react-redux';


import {DownloadButton} from 'src/components/form-buttons';
import {ToolsRibbonComponent} from 'src/components/tools-ribbon';
import {MAX_SELECTED_ENTITIES} from 'src/constants';
import {ACCESS_RIGHTS, useAdminAccessRight} from 'src/hooks/access-rights';
import {useEventTracking} from 'src/hooks/useEventTracking';

import {Group} from '@pb/kit-component-group';

import {
    SELECTION_KEY,
    UserSearchDownloadTool,
    UserSearchEditColumns,
    userSearchResultResultSelector,
} from '../../users-groups-search-result';

import {UsersGroupsBulkActions} from './bulk-actions/users-groups-bulk-actions';

import type {ChildrenProps as UsersGroupsControllerProps} from '../../users-groups-search-result/users-groups-search-result.controller';
import type {UserSearchTemplate} from 'src/openapi/admin/models/UserSearchTemplate';

type Props = {
    accountId: number,
    getAllEntityIds: UsersGroupsControllerProps['getAllEntityIds'],
    place: string,
    adminId: number,
    isLoading: boolean,
    templates: UserSearchTemplate[],
    activeSearchTemplate: UserSearchTemplate,
    setActiveSearchTemplate: (actionProps: UserSearchTemplate) => Promise<string | null>,
    loadTemplates: () => void,
    loadActiveSearchTemplate: () => Promise<void>,
    refreshResults: () => void,
};

export const UsersGroupsToolsRibbon = (props: Props) => {
    const {searchCriteriaDownload} = useEventTracking();

    const result = useSelector(userSearchResultResultSelector);
    const rows = useMemo(() => result?.data.map(({id}) => id) || [], [result]);
    const totalCount = useMemo(() => result?.totalCount || 0, [result]);

    const {hasAccess} = useAdminAccessRight();

    return (
        <ToolsRibbonComponent {...{
            selectionKey: SELECTION_KEY,
            rows,
            totalCount,
            extraTools: ids => (
                <Group>
                    <UserSearchEditColumns
                        place={props.place}
                        adminId={props.adminId}
                        isLoading={props.isLoading}
                        templates={props.templates}
                        activeSearchTemplate={props.activeSearchTemplate}
                        setActiveSearchTemplate={props.setActiveSearchTemplate}
                        loadTemplates={props.loadTemplates}
                        loadActiveSearchTemplate={props.loadActiveSearchTemplate}
                    />
                    {hasAccess(ACCESS_RIGHTS.USER_SCREEN_VIEW) ? (
                        <UserSearchDownloadTool
                            userIds={ids}
                            getSortedEntityIds={props.getAllEntityIds}>
                            {handler => (
                                <DownloadButton
                                    disabled={!ids.length}
                                    handler={() => {
                                        searchCriteriaDownload({viewType: 'User'});
                                        handler();
                                    }}
                                />
                            )}
                        </UserSearchDownloadTool>
                    ) : null}
                </Group>
            ),
            maxSelectedEntities: MAX_SELECTED_ENTITIES,
            getAllEntityIds: props.getAllEntityIds,
        }}>
            {ids => (
                <UsersGroupsBulkActions
                    accountId={props.accountId}
                    ids={ids as number[]}
                    onDone={props.refreshResults}
                />
            )}
        </ToolsRibbonComponent>
    );
};
